import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Modal } from "@fawkes/ui";

import { teamIcoon } from "./MijnTeams";

// ── Berichten & teams op het dashboard (Maarten, 2026-10-11) ────────────────────────────────────────
//
// Vanaf het dashboard een bericht sturen aan één of meer teams (of aan iedereen), en zien wat er voor jouw teams
// binnenkwam. Een bericht openen is het lezen; in het gesprek kun je reageren. De eerste regel van een bericht staat
// als titel in de lijst.
//
// Naast teams kun je ook één of meer personen kiezen (Maarten, 2026-10-15: "ik wil ook een bericht naar een persoon
// kunnen sturen!"). Personen zoek je op naam; staat dezelfde naam er twee keer in, dan zie je er het e-mailadres bij.

interface Team { code: string; naam: string; actief: boolean }
export interface Persoon { id: string; naam: string; detail: string | null }
interface Bericht {
  id: number; titel: string; tekst: string; afzender: string; initialen: string; vanMij: boolean;
  aan: string; teams: string[]; personen?: Persoon[]; alleTeams: boolean; verstuurdOp: string | null; reacties: number;
  ongelezen: number;
}
interface Reactie { id: number; tekst: string; afzender: string; initialen: string; vanMij: boolean; verstuurdOp: string | null }
interface Overzicht { berichten: Bericht[]; ongelezen: number }
interface Gesprek { bericht: Bericht; reacties: Reactie[] }

type T = (key: string, options?: { args?: string[] }) => string;

const URL = "team-berichten";
const MAX_OP_DASHBOARD = 4;
const MAX_TEKST = 2000;

/** "10 min geleden", "2 uur geleden", "gisteren", en daarna de datum. */
export function hoeLangGeleden(iso: string | null, t: T, nu: Date = new Date()): string {
  if (!iso) return "";
  const toen = new Date(iso);
  if (Number.isNaN(toen.getTime())) return "";
  const minuten = Math.floor((nu.getTime() - toen.getTime()) / 60000);
  if (minuten < 1) return t("berichten.zojuist");
  if (minuten < 60) return t("berichten.minGeleden", { args: [String(minuten)] });
  const uren = Math.floor(minuten / 60);
  if (uren < 24) return t("berichten.uurGeleden", { args: [String(uren)] });
  if (uren < 48) return t("berichten.gisteren");
  return toen.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function aanWie(b: Bericht, t: T): string {
  return b.alleTeams ? t("berichten.alleTeams") : b.aan;
}

function initialenVan(naam: string): string {
  const delen = naam.trim().split(/\s+/).filter(Boolean);
  if (delen.length === 0) return "?";
  return ((delen[0][0] ?? "") + (delen.length > 1 ? delen[delen.length - 1][0] : "")).toUpperCase();
}

/** De naam zoals hij in de kiezer staat: met e-mail erbij als dezelfde naam vaker voorkomt. */
export function persoonLabel(p: Persoon, iedereen: Persoon[]): string {
  const dubbel = iedereen.some((x) => x.id !== p.id && x.naam.toLowerCase() === p.naam.toLowerCase());
  return dubbel && p.detail ? `${p.naam} (${p.detail})` : p.naam;
}

/** Zoeken op naam of e-mail; wie al gekozen is valt weg. */
export function zoekPersonen(iedereen: Persoon[], zoek: string, gekozen: string[], max = 8): Persoon[] {
  const z = zoek.trim().toLowerCase();
  if (!z) return [];
  return iedereen
    .filter((p) => !gekozen.includes(p.id))
    .filter((p) => p.naam.toLowerCase().includes(z) || (p.detail ?? "").toLowerCase().includes(z))
    .slice(0, max);
}

/** Een persoon zoeken en toevoegen als ontvanger. */
function PersoonKiezer({ iedereen, gekozen, t, onKies }: {
  iedereen: Persoon[]; gekozen: string[]; t: T; onKies: (p: Persoon) => void;
}) {
  const [zoek, setZoek] = useState("");
  const [actief, setActief] = useState(0);
  const treffers = zoekPersonen(iedereen, zoek, gekozen);
  const kies = (p: Persoon) => { onKies(p); setZoek(""); setActief(0); };
  return (
    <div className="tbr-persoon">
      <input className="fui-field__input tbr-persoon__zoek" type="search" value={zoek} role="combobox"
        aria-label={t("berichten.persoonZoeken")} placeholder={t("berichten.persoonPlaceholder")}
        aria-expanded={treffers.length > 0} aria-controls="tbr-persoon-lijst" aria-autocomplete="list"
        onChange={(e) => { setZoek(e.target.value); setActief(0); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setActief((a) => Math.min(a + 1, treffers.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActief((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter" && treffers[actief]) { e.preventDefault(); kies(treffers[actief]); }
          else if (e.key === "Escape") setZoek("");
        }} />
      {zoek.trim() !== "" && (
        <ul className="tbr-persoon__lijst" id="tbr-persoon-lijst" role="listbox">
          {treffers.length === 0 ? (
            <li className="tbr-persoon__leeg">{t("berichten.persoonGeen")}</li>
          ) : treffers.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === actief}
              className={i === actief ? "tbr-persoon__optie--actief" : undefined}
              onMouseDown={(e) => { e.preventDefault(); kies(p); }}>
              <strong>{p.naam}</strong>
              {p.detail && <small>{p.detail}</small>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BerichtRij({ b, t, onOpen }: { b: Bericht; t: T; onOpen: () => void }) {
  return (
    <li>
      <button type="button" className={`tbr-rij${b.ongelezen > 0 ? " tbr-rij--nieuw" : ""}`} onClick={onOpen}>
        <span className="tbr-avatar" aria-hidden="true">{b.initialen}</span>
        <span className="tbr-rij__tekst">
          <strong>{b.titel}</strong>
          <small>
            {b.vanMij ? t("berichten.vanMij") : b.afzender} · {t("berichten.aan", { args: [aanWie(b, t)] })}
            {b.verstuurdOp ? ` · ${hoeLangGeleden(b.verstuurdOp, t)}` : ""}
          </small>
        </span>
        {b.reacties > 0 && (
          <span className="tbr-reacties" title={t("berichten.reacties", { args: [String(b.reacties)] })}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
            </svg>
            {b.reacties}
          </span>
        )}
        {b.ongelezen > 0 && <span className="tbr-stip" aria-label={t("berichten.ongelezenEen")} />}
      </button>
    </li>
  );
}

/** Het paneel op het dashboard. */
export function TeamBerichtenPanel() {
  const t = useTranslate() as T;
  const { data, refetch } = useCustom<Overzicht>({ url: URL, method: "get" });
  const { data: teamData } = useCustom<Team[]>({ url: "taak-teams", method: "get" });
  const { data: persoonData } = useCustom<Persoon[]>({ url: `${URL}/ontvangers`, method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [naar, setNaar] = useState<string[]>([]);
  const [personen, setPersonen] = useState<Persoon[]>([]);
  const [tekst, setTekst] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  const [verstuurd, setVerstuurd] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [alles, setAlles] = useState(false);

  const teams = Array.isArray(teamData?.data) ? teamData!.data.filter((x) => x.actief !== false) : [];
  const berichten = Array.isArray(data?.data?.berichten) ? data!.data.berichten : [];
  const ongelezen = data?.data?.ongelezen ?? 0;
  const iedereen = Array.isArray(persoonData?.data) ? persoonData!.data : [];
  const naarIedereen = naar.length === 0 && personen.length === 0;

  const wissel = (code: string) => {
    setVerstuurd(false);
    setNaar((oud) => (oud.includes(code) ? oud.filter((c) => c !== code) : [...oud, code]));
  };
  const verstuur = () => {
    if (!tekst.trim()) {
      setFout(t("berichten.typEerst"));
      return;
    }
    const values = { tekst, teams: naar, personen: personen.map((p) => p.id) };
    mutate({ url: URL, method: "post", values, successNotification: false, errorNotification: false }, {
      onSuccess: () => { setTekst(""); setNaar([]); setPersonen([]); setFout(null); setVerstuurd(true); void refetch(); },
      onError: () => { setVerstuurd(false); setFout(t("berichten.mislukt")); },
    });
  };

  return (
    <div className="fcc-panel fcc-panel--berichten">
      <div className="fcc-panel-head">
        <h2 className="fcc-panel-title">{t("berichten.titel")}</h2>
        {ongelezen > 0 && (
          <div className="fcc-badge tbr-badge">
            {t(ongelezen === 1 ? "berichten.ongelezenBadgeEen" : "berichten.ongelezenBadge", { args: [String(ongelezen)] })}
          </div>
        )}
      </div>

      <div className="tbr-nieuw">
        <span className="tbr-label" id="tbr-naar">{t("berichten.versturenNaar")}</span>
        <div className="tbr-chips" role="group" aria-labelledby="tbr-naar">
          <button type="button" className={`tbr-chip${naarIedereen ? " tbr-chip--aan" : ""}`} aria-pressed={naarIedereen}
            onClick={() => { setVerstuurd(false); setNaar([]); setPersonen([]); }}>
            {t("berichten.alleTeamsChip")}
          </button>
          {teams.map((x) => (
            <button key={x.code} type="button" className={`tbr-chip${naar.includes(x.code) ? " tbr-chip--aan" : ""}`}
              aria-pressed={naar.includes(x.code)} onClick={() => wissel(x.code)}>
              {teamIcoon(x.naam) && <span aria-hidden="true">{teamIcoon(x.naam)}</span>}
              {x.naam}
            </button>
          ))}
        </div>
        <div className="tbr-personen">
          {personen.length > 0 && (
            <ul className="tbr-chips" aria-label={t("berichten.gekozenPersonen")}>
              {personen.map((p) => (
                <li key={p.id} className="tbr-chip tbr-chip--aan tbr-chip--persoon">
                  <span className="tbr-chip__avatar" aria-hidden="true">{initialenVan(p.naam)}</span>
                  {persoonLabel(p, iedereen)}
                  <button type="button" className="tbr-chip__weg" aria-label={t("berichten.persoonWeg", { args: [p.naam] })}
                    onClick={() => { setVerstuurd(false); setPersonen((oud) => oud.filter((x) => x.id !== p.id)); }}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <PersoonKiezer iedereen={iedereen} gekozen={personen.map((p) => p.id)} t={t}
            onKies={(p) => { setVerstuurd(false); setPersonen((oud) => [...oud, p]); }} />
        </div>
        <textarea className="fui-field__input tbr-tekst" rows={3} maxLength={MAX_TEKST} value={tekst}
          aria-label={t("berichten.tekstLabel")} placeholder={t("berichten.placeholder")}
          onChange={(e) => { setTekst(e.target.value); setFout(null); setVerstuurd(false); }} />
        <div className="tbr-nieuw__voet">
          <span className="tbr-hint" role="status">{verstuurd ? t("berichten.verstuurd") : t("berichten.hint")}</span>
          <Button variant="primary" onClick={verstuur} disabled={bezig}>{t("berichten.versturen")}</Button>
        </div>
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </div>

      <div className="tbr-recent">
        <span className="tbr-label">{t("berichten.recent")}</span>
        {berichten.length === 0 ? (
          <p className="tbr-leeg">{t("berichten.geen")}</p>
        ) : (
          <ul className="tbr-lijst">
            {berichten.slice(0, MAX_OP_DASHBOARD).map((b) => <BerichtRij key={b.id} b={b} t={t} onOpen={() => setOpenId(b.id)} />)}
          </ul>
        )}
      </div>
      {berichten.length > 0 && (
        <button type="button" className="fcc-link" onClick={() => setAlles(true)}>
          <span>{t("berichten.allesBekijken")}</span>
          <span aria-hidden="true">→</span>
        </button>
      )}

      {alles && (
        <Modal open title={t("berichten.alleBerichten")} onClose={() => setAlles(false)}
          footer={<Button variant="secondary" onClick={() => setAlles(false)}>{t("common.close")}</Button>}>
          <ul className="tbr-lijst tbr-lijst--venster">
            {berichten.map((b) => <BerichtRij key={b.id} b={b} t={t} onOpen={() => { setAlles(false); setOpenId(b.id); }} />)}
          </ul>
        </Modal>
      )}
      {openId != null && <GesprekVenster id={openId} t={t} onSluit={() => { setOpenId(null); void refetch(); }} />}
    </div>
  );
}

/** Eén bericht met zijn reacties; openen markeert het als gelezen. */
function GesprekVenster({ id, t, onSluit }: { id: number; t: T; onSluit: () => void }) {
  const { data, refetch, isError } = useCustom<Gesprek>({ url: `${URL}/${id}`, method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [tekst, setTekst] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  const g = data?.data;
  const b = g?.bericht;
  const reacties = Array.isArray(g?.reacties) ? g!.reacties : [];

  const reageer = () => {
    if (!tekst.trim()) {
      setFout(t("berichten.typEerst"));
      return;
    }
    mutate({ url: `${URL}/${id}/reacties`, method: "post", values: { tekst }, successNotification: false, errorNotification: false }, {
      onSuccess: () => { setTekst(""); setFout(null); void refetch(); },
      onError: () => setFout(t("berichten.mislukt")),
    });
  };

  return (
    <Modal open title={b?.titel ?? t("berichten.bericht")} onClose={onSluit}
      footer={<Button variant="secondary" onClick={onSluit}>{t("common.close")}</Button>}>
      {isError && <p className="fawkes-error" role="alert">{t("berichten.nietGevonden")}</p>}
      {b && (
        <div className="tbr-gesprek">
          <Balk initialen={b.initialen} afzender={b.vanMij ? t("berichten.vanMij") : b.afzender} tekst={b.tekst} vanMij={b.vanMij}
            onder={`${t("berichten.aan", { args: [aanWie(b, t)] })}${b.verstuurdOp ? ` · ${hoeLangGeleden(b.verstuurdOp, t)}` : ""}`} />
          {reacties.map((r) => (
            <Balk key={r.id} initialen={r.initialen} afzender={r.vanMij ? t("berichten.vanMij") : r.afzender} tekst={r.tekst}
              vanMij={r.vanMij} onder={hoeLangGeleden(r.verstuurdOp, t)} reactie />
          ))}
          <div className="tbr-reageer">
            <textarea className="fui-field__input tbr-tekst" rows={2} maxLength={MAX_TEKST} value={tekst}
              aria-label={t("berichten.reageerLabel")} placeholder={t("berichten.reageerPlaceholder")}
              onChange={(e) => { setTekst(e.target.value); setFout(null); }} />
            <Button variant="primary" onClick={reageer} disabled={bezig}>{t("berichten.reageren")}</Button>
          </div>
          {fout && <p className="fawkes-error" role="alert">{fout}</p>}
        </div>
      )}
    </Modal>
  );
}

function Balk({ initialen, afzender, tekst, onder, vanMij, reactie }: {
  initialen: string; afzender: string; tekst: string; onder: string; vanMij: boolean; reactie?: boolean;
}) {
  return (
    <div className={`tbr-balk${reactie ? " tbr-balk--reactie" : ""}${vanMij ? " tbr-balk--mij" : ""}`}>
      <span className="tbr-avatar" aria-hidden="true">{initialen}</span>
      <div className="tbr-balk__inhoud">
        <strong>{afzender}</strong>
        <p>{tekst}</p>
        {onder && <small>{onder}</small>}
      </div>
    </div>
  );
}
